import React, { useState, useEffect, useRef } from "react"
import { CONSTANTS } from "@data"
import { css, easeInOutCubic, mapRange, useSassUnitValues } from "@jsUtils"
import { CTA, Eyebrow, GridLines, Headline, CirclesBurstSet } from "@shared"
import GlobalStyles from "@utils"
import { useInView } from "react-intersection-observer"
import { gsap, Power1 } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { PageSection } from "../PageSection"
import { VideoOverlay } from "../VideoOverlay"
import Styles from "./workflowSteps.module.scss"

gsap.registerPlugin(ScrollTrigger)

const { COLORS, BREAKPOINTS } = CONSTANTS
const MOBILE_BREAKPOINT = useSassUnitValues(BREAKPOINTS.MOBILE)

const Slider = props => {
  const { labels, ready, isMobile, inView, progress, scrollTrigger } = props
  const [initDone, setInitDone] = useState(false)
  const refs = useRef([])
  const getMapped = () => mapRange(progress * 100, 0, 100, 0, 99.5)

  useEffect(() => {
    if (!scrollTrigger) return
    scrollTrigger.to(refs.current[0], {
      opacity: 1,
      scale: 1,
      duration: 0.5
    })
    scrollTrigger.to(
      refs.current[1],
      {
        opacity: 1,
        scale: 1,
        duration: 0.5
      },
      "+=5%"
    )
    scrollTrigger.to(
      refs.current[2],
      {
        opacity: 1,
        scale: 1,
        duration: 0.5
      },
      "+=5%"
    )
    scrollTrigger.to(
      refs.current[3],
      {
        opacity: 1,
        scale: 1,
        duration: 0.5
      },
      "+=5%"
    )
  }, [scrollTrigger])

  return (
    <>
      <div className={Styles.sliderWrapper}>
        <input
          type="range"
          className={Styles.slider}
          min={0}
          max={100}
          step={0.01}
          value={progress * 100}
          // onChange={e => setValue(e.target.value)}
          style={{
            background: `linear-gradient(to right, #6864dd, #80bce7 ${getMapped()}%, #eaeaea ${getMapped()}%, #eaeaea 100%)`
          }}
          disabled
        />
      </div>
      <div className={css(Styles.labelWrapper, ready && Styles.ready)}>
        {labels.map((label, idx) => {
          return (
            <div
              ref={el => {
                refs.current[idx] = el
              }}
              // eslint-disable-next-line react/no-array-index-key
              key={`${idx}-${idx}`}
              className={css(Styles.labelItem)}
            >
              <Headline size={3}>{`0${idx + 1}`}</Headline>
              <Headline size={5}>{label}</Headline>
            </div>
          )
        })}
      </div>
    </>
  )
}

// eslint-disable-next-line react/no-multi-comp
const WorkflowSteps = props => {
  const { theme, color, data } = props
  const [ready, setReady] = useState(false)
  const [mobileLayout, setMobileLayout] = useState(false)
  const [vidOpen, setVidOpen] = useState(false)
  const [progress, setProgress] = useState(0)
  const progressRef = useRef(0)
  const ref = useRef()
  const [scrollTrigger, setScrollTrigger] = useState()

  useEffect(() => {
    let tl
    const element = ref.current
    if (element) {
      const matchMedia = {}
      matchMedia[`(min-width: 1px)`] = () => {
        tl = new gsap.timeline({
          scrollTrigger: {
            trigger: element,
            pin: true,
            scrub: 0.9,
            onUpdate: self => {
              setProgress(self.progress)
            }
          }
        })
        setScrollTrigger(tl)
      }
      matchMedia[`(min-width: ${MOBILE_BREAKPOINT}px)`] = () => {
        tl = new gsap.timeline({
          scrollTrigger: {
            trigger: element,
            pin: true,
            scrub: 1,
            onUpdate: self => {
              setProgress(self.progress)
            }
          }
        })
        setScrollTrigger(tl)
      }
      ScrollTrigger.matchMedia(matchMedia)
    }
    return () => {
      tl?.kill()
      element.styles = {}
    }
  }, [ref])

  useEffect(() => {
    return () => {
      const element = ref.current
      if (element) {
        const parent = element.parentNode
        if (parent.classList.contains("pin-spacer")) {
          const grandparent = parent?.parentNode
          if (grandparent) {
            grandparent.insertBefore(element, parent)
            try {
              parent?.remove()
            } catch {
              console.warn("Failed to Remove Wrapper from Workflow Steps Section")
            }
          }
        }
      }
    }
  }, [ref])

  progressRef.current = progress

  const onResize = () => {
    if (window.innerWidth < MOBILE_BREAKPOINT && !mobileLayout) {
      setMobileLayout(true)
    } else if (window.innerWidth >= MOBILE_BREAKPOINT && mobileLayout) {
      setMobileLayout(false)
    }
  }

  useEffect(() => {
    onResize()
    window.addEventListener("resize", onResize)
    return () => {
      window.removeEventListener("resize", onResize)
    }
  }, [])

  const handleOpen = () => {
    setVidOpen(true)
    document.getElementsByTagName("body")[0].classList.add(GlobalStyles.noScroll)
  }

  const handleClose = () => {
    setVidOpen(false)
    document.getElementsByTagName("body")[0].classList.remove(GlobalStyles.noScroll)
  }

  return (
    <>
      <div className={Styles.pinned} ref={ref}>
        <PageSection
          className={css(Styles.section, ready && Styles.ready)}
          theme={theme}
          ready={ready}
          onReady={() => {
            setReady(true)
          }}
        >
          <GridLines theme={theme} />
          <CirclesBurstSet className={Styles.circles} color={color} ready={ready} />
          <div className={Styles.wrapper}>
            <div className={Styles.header}>
              <Eyebrow className={Styles.eyebrow} color={color}>
                {data.eyebrow}
              </Eyebrow>
              <Headline size={2} className={Styles.headline}>
                {data.headline && data.headline.split("|")[0]}
                <div className={Styles.headlineSub}>
                  {data.headline && data.headline.split("|")[1]}
                </div>
              </Headline>
            </div>
            {data && data.workflowsteps && (
              <Slider
                inView
                labels={data.workflowsteps}
                ready={ready}
                isMobile={mobileLayout}
                progress={progress}
                scrollTrigger={scrollTrigger}
              />
            )}
            <CTA
              ready={ready}
              className={Styles.cta}
              color={COLORS.WORKS}
              content="play"
              ascope="WORKFLOWSTEPS"
              adata="MISC.VIDEO"
              isBtn
              onClick={handleOpen}
            >
              watch the video
            </CTA>
            <p className={Styles.footer}>
              Exo Works is Epic App Orchard certified and Cerner CODE program approved.
              <br />
              Easily sync with your EMR.
            </p>
          </div>
        </PageSection>
      </div>
      {vidOpen && (
        <VideoOverlay
          onClose={handleClose}
          videoURL="https://exo-inc-videos.s3.us-west-1.amazonaws.com/Experience+the+Freedom+of+Exo+Works.mp4"
        />
      )}
    </>
  )
}

export default WorkflowSteps
